// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Peer lineup for the alarm detail's "Floor plan" panel.
 *
 * Despite the panel's label, this is not a spatial plan — the twin carries no
 * coordinates, and no `hasLocation`/`isLocationOf` relationship is ever
 * populated. What the design actually draws is the alarming asset's *peer
 * group*, each peer showing one headline value. See
 * docs/design/alarm-detail-floor-plan-widget.md for the investigation.
 *
 * Peers are siblings in the power-flow graph: entities sharing an immediate
 * upstream parent with the focus, restricted to the focus's own entity type.
 * That is the only grouping the available relationships (`feeds`,
 * `hasTelemetryPoint`) can support. Sources — utility feeders, generators, BESS
 * — have no upstream parent, so for those it widens to same-type assets across
 * the site rather than showing nothing.
 *
 * Direction comes from `normalizeFlowRelationship` (see `flowRelationships.ts`), the
 * same tested helper `equipmentFlowGraph` uses, so inverse relationship types
 * can't silently flip the graph.
 */

import type { Entity, Relationship, ValueEnvelope } from '../types/digitalTwin';
import type { FloorCell } from '../types/alarm';
import type { StatusTone } from './statusTone';
import { normalizeFlowRelationship } from './flowRelationships';
import { assetName } from './entityLabels';
import {
  categorizeStatus,
  getEntityDisplayType,
  getEntityStatus,
  getStatusToken,
  isAssetEntityType,
  type StatusCategory,
} from './entityHelpers';

export interface AssetLineup {
  /** Grouping label, e.g. the shared parent's label or the equipment type. */
  title: string;
  /** What the lineup is and what the cell values mean. */
  subtitle: string;
  cells: FloorCell[];
  /** Peers beyond `maxCells`, for a "+N" affordance. */
  overflow: number;
}

export interface BuildAssetLineupOptions {
  /** Cells rendered before the remainder is counted as overflow. Default 6. */
  maxCells?: number;
}

const DEFAULT_MAX_CELLS = 6;

/** Short token shown when a peer has no numeric reading to display. */
const STATUS_VAL: Record<StatusCategory, string> = {
  healthy: 'OK',
  warning: 'WARN',
  alarming: 'FLT',
  offline: 'STBY',
  unknown: '—',
};

const entityLabel = (e: Entity): string => assetName(e, e.id);

const entityType = (e: Entity): string => e.type ?? '';

const headlineReading = (e: Entity): string | null => {
  const state = e.state;
  if (!state) return null;
  for (const [key, raw] of Object.entries(state)) {
    if (key === 'status' || key === 'updatedAt') continue;
    if (!raw || typeof raw !== 'object' || !('value' in raw)) continue;
    const env = raw as ValueEnvelope;
    const isTemp =
      /temp/i.test(key) || /temp/i.test(String(env.quantityKind ?? '')) || env.unit === '°F';
    if (!isTemp || typeof env.value !== 'number') continue;
    return String(Math.round(env.value));
  }
  return null;
};

const toneOf = (e: Entity): StatusTone => getStatusToken(categorizeStatus(getEntityStatus(e)));

const toCell = (e: Entity, focus: boolean): FloorCell => {
  const category = categorizeStatus(getEntityStatus(e));
  const reading = category === 'alarming' || category === 'offline' ? null : headlineReading(e);
  return {
    id: entityLabel(e),
    val: reading ?? STATUS_VAL[category],
    tone: toneOf(e),
    ...(focus ? { focus: true } : {}),
  };
};

/**
 * Assemble the peer lineup for one asset.
 *
 * Returns `null` — so the caller renders nothing at all, no empty titled shell —
 * when the focus isn't in the graph, or when it has no same-type siblings. A
 * "lineup" of one asset is not a lineup, and the panel would carry no
 * information the header doesn't already show.
 */
export const buildAssetLineup = (
  focusId: string,
  entities: Entity[],
  relationships: Relationship[],
  { maxCells = DEFAULT_MAX_CELLS }: BuildAssetLineupOptions = {}
): AssetLineup | null => {
  if (!focusId) return null;

  const byId = new Map(entities.map(e => [e.id, e]));
  const focus = byId.get(focusId);
  if (!focus) return null;

  const children = new Map<string, Set<string>>();
  const parents = new Map<string, Set<string>>();
  for (const rel of relationships) {
    const flow = normalizeFlowRelationship(rel);
    if (!flow) continue;
    (children.get(flow.source) ?? children.set(flow.source, new Set()).get(flow.source)!).add(
      flow.target
    );
    (parents.get(flow.target) ?? parents.set(flow.target, new Set()).get(flow.target)!).add(
      flow.source
    );
  }

  const focusType = entityType(focus);
  const parentIds = Array.from(parents.get(focusId) ?? []);

  const siblingIds = new Set<string>();
  for (const parentId of parentIds) {
    for (const childId of Array.from(children.get(parentId) ?? [])) {
      if (childId === focusId) continue;
      const child = byId.get(childId);
      if (child && entityType(child) === focusType) siblingIds.add(childId);
    }
  }

  const scopedToParent = siblingIds.size > 0;
  if (!scopedToParent && isAssetEntityType(focusType)) {
    for (const e of entities) {
      if (e.id !== focusId && entityType(e) === focusType) siblingIds.add(e.id);
    }
  }
  if (siblingIds.size === 0) return null;

  const siblings = Array.from(siblingIds)
    .map(id => byId.get(id)!)
    .sort((a, b) => entityLabel(a).localeCompare(entityLabel(b)) || a.id.localeCompare(b.id));

  const ordered = [focus, ...siblings].sort(
    (a, b) => entityLabel(a).localeCompare(entityLabel(b)) || a.id.localeCompare(b.id)
  );

  const shown = ordered.slice(0, maxCells);
  const cells = shown.map(e => toCell(e, e.id === focusId));

  const soleParent = scopedToParent && parentIds.length === 1 ? byId.get(parentIds[0]) : undefined;
  const typeLabel = getEntityDisplayType(focusType);
  const title = soleParent ? entityLabel(soleParent) : typeLabel;

  return {
    title,
    subtitle: `${typeLabel} lineup · ${cells.length} of ${ordered.length}`,
    cells,
    overflow: ordered.length - shown.length,
  };
};
